/**
 * StrategyCanvasPage
 *
 * Full-screen page for viewing/editing a strategy canvas via direct URL.
 * Follows the OnboardingEditorPageV2 pattern: renders outside Layout (no sidebar).
 *
 * Route: /project/:projectId/strategy/:strategyId
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Alert, Button, CircularProgress } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { BlockCanvasEditor } from '@modules/reactFlow/components/shared/BlockCanvasEditor';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import {
  buildProjectUrl,
  buildStrategyBlockScreenModalUrl,
  buildStrategyBlockModalUrl,
  buildStrategyCanvasUrl,
  PROJECT_TABS,
  STRATEGY_BLOCK_MODAL_PARAM,
  STRATEGY_SCREEN_MODAL_PARAM,
} from '@modules/navigation/constants/routes';

export function StrategyCanvasPage() {
  const { projectId, strategyId } = useParams<{ projectId: string; strategyId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [strategyName, setStrategyName] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const initialScreensBlockId = useMemo(() => (
    searchParams.get(STRATEGY_BLOCK_MODAL_PARAM)
    || searchParams.get('block')
    || null
  ), [searchParams]);
  const initialScreensScreenId = useMemo(() => (
    searchParams.get(STRATEGY_SCREEN_MODAL_PARAM)
    || searchParams.get('screen')
    || null
  ), [searchParams]);

  // Fetch strategy name for display in canvas header
  useEffect(() => {
    if (!projectId || !strategyId) return;

    const service = new StrategyService(projectId);
    service.get(strategyId)
      .then((strategy) => {
        if (strategy) {
          setStrategyName(strategy.name);
        } else {
          setError('Strategy not found');
        }
      })
      .catch((err) => {
        console.error('[StrategyCanvasPage] Failed to load strategy:', err);
        setError('Failed to load strategy');
      })
      .finally(() => setLoading(false));
  }, [projectId, strategyId]);

  const handleClose = () => {
    if (projectId) {
      navigate(buildProjectUrl(projectId, PROJECT_TABS.STRATEGIES));
    } else {
      navigate(-1);
    }
  };

  const handleOpenBlockScreens = useCallback((blockId: string) => {
    if (!projectId || !strategyId) return;
    navigate(buildStrategyBlockModalUrl(projectId, strategyId, blockId));
  }, [navigate, projectId, strategyId]);

  const handleScreensModalClose = useCallback(() => {
    if (!projectId || !strategyId) return;
    navigate(buildStrategyCanvasUrl(projectId, strategyId), { replace: true });
  }, [navigate, projectId, strategyId]);

  const handleOpenBlockScreen = useCallback((blockId: string, screenId: string) => {
    if (!projectId || !strategyId) return;
    navigate(buildStrategyBlockScreenModalUrl(projectId, strategyId, blockId, screenId), { replace: true });
  }, [navigate, projectId, strategyId]);

  const handleScreenEditorClose = useCallback(() => {
    if (!projectId || !strategyId || !initialScreensBlockId) return;
    navigate(buildStrategyBlockModalUrl(projectId, strategyId, initialScreensBlockId), { replace: true });
  }, [initialScreensBlockId, navigate, projectId, strategyId]);

  if (!projectId || !strategyId) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">Missing project or strategy ID</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleClose} sx={{ mb: 2 }}>
          Back to Strategies
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <BlockCanvasEditor
      open={true}
      onClose={handleClose}
      projectId={projectId}
      strategyId={strategyId}
      strategyName={strategyName}
      initialScreensBlockId={initialScreensBlockId}
      initialScreensScreenId={initialScreensScreenId}
      onOpenBlockScreens={handleOpenBlockScreens}
      onScreensModalClose={handleScreensModalClose}
      onOpenBlockScreen={handleOpenBlockScreen}
      onScreenEditorClose={handleScreenEditorClose}
    />
  );
}

export default StrategyCanvasPage;
